// import React, { useCallback, useEffect, useRef, useState } from 'react';
// import Select from 'react-select';

// // ─────────────────────────────────────────────────────────────────────────────
// // TYPES
// // ─────────────────────────────────────────────────────────────────────────────

// interface Party {
//   id: number | string;
//   name: string;
//   categories?: string[];
//   total_amount?: number | string;
// }

// interface OcrExtractedRow {
//   id: string;
//   stock_name: string;
//   unit: string;
//   total_amount: string;
//   indirect_charges: string;
//   per_unit_charge: string;
//   confidence: 'high' | 'medium' | 'low';
//   raw_text?: string;
//   edited?: boolean;
//   deleted?: boolean;
// }

// interface OcrResult {
//   party_name?: string;
//   category?: string;
//   date?: string;
//   rows: OcrExtractedRow[];
//   raw_response?: string;
//   doc_type?: string;
//   confidence_overall?: number;
// }

// interface OcrFile {
//   file: File;
//   id: string;
//   preview?: string;
//   status: 'pending' | 'processing' | 'done' | 'error';
//   result?: OcrResult;
//   error?: string;
// }

// interface Props {
//   partiesList: Party[];
//   stocksnames: { id: number; name: string }[];
//   mutualsnames: { id: number; name: string }[];
//   onSubmit: (payload: {
//     partyId: string;
//     category: string;
//     date: string;
//     rows: OcrExtractedRow[];
//   }) => Promise<void>;
//   onClose: () => void;
//   apiUrl: string;
// }

// // ─────────────────────────────────────────────────────────────────────────────
// // HELPERS
// // ─────────────────────────────────────────────────────────────────────────────

// const safeNum = (v: any) => {
//   const n = parseFloat(v);
//   return isNaN(n) ? 0 : n;
// };

// const uid = () => Math.random().toString(36).slice(2, 9);

// const fileToBase64 = (file: File): Promise<string> =>
//   new Promise((res, rej) => {
//     const r = new FileReader();
//     r.onload = () => res((r.result as string).split(',')[1]);
//     r.onerror = () => rej(new Error('Read failed'));
//     r.readAsDataURL(file);
//   });

// const SUPPORTED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'application/pdf'];

// // OCR prompt for Claude
// const buildOcrPrompt = (fileCount: number) => `
// You are an expert financial document OCR system. Extract ALL stock/investment data from the provided document(s).

// Return ONLY a valid JSON object with this exact structure (no markdown, no extra text):
// {
//   "party_name": "extracted party or client name if visible, else null",
//   "category": "detected category: Equity / Mutual Funds / FNO / ETF / else null",
//   "date": "purchase date in YYYY-MM-DD format if found, else null",
//   "doc_type": "brief description of document type e.g. contract note, portfolio statement",
//   "confidence_overall": <0-100 integer>,
//   "rows": [
//     {
//       "stock_name": "exact stock/fund name",
//       "unit": "quantity/units as string number",
//       "total_amount": "total rupee amount as string number (no commas, no ₹)",
//       "indirect_charges": "brokerage/charges/STT/taxes as string number, 0 if none",
//       "per_unit_charge": "price per unit as string number, compute if missing",
//       "confidence": "high|medium|low",
//       "raw_text": "the exact text segment this row was extracted from"
//     }
//   ]
// }

// Rules:
// - Extract EVERY transaction/stock row you find
// - Remove commas from numbers (1,234.56 → 1234.56)
// - If per_unit_charge is missing, compute: (total_amount + indirect_charges) / unit
// - confidence = high if clearly readable, medium if inferred, low if guessed
// - Return empty array for rows if no stock data found
// - NEVER add any text before or after the JSON
// `;

// // ─────────────────────────────────────────────────────────────────────────────
// // OCR API CALL
// // ─────────────────────────────────────────────────────────────────────────────

// async function runOcrOnFile(file: File): Promise<OcrResult> {
//   const isImage = file.type.startsWith('image/');
//   const isPdf = file.type === 'application/pdf';
//   const base64 = await fileToBase64(file);

//   const contentBlock = isPdf
//     ? {
//         type: 'document',
//         source: { type: 'base64', media_type: 'application/pdf', data: base64 },
//       }
//     : {
//         type: 'image',
//         source: { type: 'base64', media_type: file.type, data: base64 },
//       };

//   const response = await fetch('https://api.anthropic.com/v1/messages', {
//     method: 'POST',
//     headers: { 'Content-Type': 'application/json' },
//     body: JSON.stringify({
//       model: 'claude-sonnet-4-20250514',
//       max_tokens: 4096,
//       messages: [
//         {
//           role: 'user',
//           content: [
//             contentBlock,
//             { type: 'text', text: buildOcrPrompt(1) },
//           ],
//         },
//       ],
//     }),
//   });

//   if (!response.ok) throw new Error(`API error: ${response.status}`);
//   const data = await response.json();
//   const text = data.content?.map((b: any) => b.text || '').join('') || '';

//   // Strip any markdown fences
//   const clean = text.replace(/```json|```/g, '').trim();
//   const parsed = JSON.parse(clean) as OcrResult;

//   // Ensure rows have IDs
//   parsed.rows = (parsed.rows || []).map(r => ({
//     ...r,
//     id: uid(),
//     indirect_charges: r.indirect_charges || '0',
//     confidence: r.confidence || 'medium',
//   }));

//   return parsed;
// }

// // ─────────────────────────────────────────────────────────────────────────────
// // CONFIDENCE BADGE
// // ─────────────────────────────────────────────────────────────────────────────

// const ConfidenceBadge: React.FC<{ level: 'high' | 'medium' | 'low' }> = ({ level }) => {
//   const map = {
//     high: { bg: '#DCFCE7', color: '#166534', icon: '●', label: 'High' },
//     medium: { bg: '#FEF9C3', color: '#854D0E', icon: '◐', label: 'Medium' },
//     low: { bg: '#FEE2E2', color: '#991B1B', icon: '○', label: 'Low' },
//   };
//   const s = map[level];
//   return (
//     <span
//       style={{
//         display: 'inline-flex', alignItems: 'center', gap: 4,
//         background: s.bg, color: s.color,
//         fontSize: 11, fontWeight: 500,
//         padding: '2px 8px', borderRadius: 99,
//       }}
//     >
//       <span>{s.icon}</span>{s.label}
//     </span>
//   );
// };

// // ─────────────────────────────────────────────────────────────────────────────
// // STEP INDICATOR
// // ─────────────────────────────────────────────────────────────────────────────

// const StepBar: React.FC<{ step: number }> = ({ step }) => {
//   const steps = ['Upload', 'OCR Scan', 'Review & Edit', 'Confirm'];
//   return (
//     <div style={{ display: 'flex', alignItems: 'center', gap: 0, padding: '0 24px', marginTop: 4 }}>
//       {steps.map((label, i) => {
//         const done = i < step;
//         const active = i === step;
//         return (
//           <React.Fragment key={i}>
//             <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
//               <div style={{
//                 width: 28, height: 28, borderRadius: '50%',
//                 background: done ? '#10B981' : active ? '#6366F1' : '#E5E7EB',
//                 color: done || active ? '#fff' : '#9CA3AF',
//                 display: 'flex', alignItems: 'center', justifyContent: 'center',
//                 fontSize: 12, fontWeight: 600,
//                 transition: 'all 0.3s',
//               }}>
//                 {done ? '✓' : i + 1}
//               </div>
//               <span style={{ fontSize: 11, color: active ? '#6366F1' : done ? '#10B981' : '#9CA3AF', fontWeight: active ? 600 : 400, whiteSpace: 'nowrap' }}>
//                 {label}
//               </span>
//             </div>
//             {i < steps.length - 1 && (
//               <div style={{ flex: 1, height: 2, background: done ? '#10B981' : '#E5E7EB', margin: '0 4px', marginBottom: 20, transition: 'all 0.3s' }} />
//             )}
//           </React.Fragment>
//         );
//       })}
//     </div>
//   );
// };

// // ─────────────────────────────────────────────────────────────────────────────
// // MAIN OCR MODAL COMPONENT
// // ─────────────────────────────────────────────────────────────────────────────

// const OcrUploadModal: React.FC<Props> = ({
//   partiesList, stocksnames, mutualsnames, onSubmit, onClose, apiUrl,
// }) => {
//   // Step: 0=upload, 1=scanning, 2=review, 3=confirm
//   const [step, setStep] = useState(0);
//   const [files, setFiles] = useState<OcrFile[]>([]);
//   const [dragging, setDragging] = useState(false);
//   const [selectedPartyId, setSelectedPartyId] = useState('');
//   const [selectedCategory, setSelectedCategory] = useState('');
//   const [overrideDate, setOverrideDate] = useState('');
//   const [mergedRows, setMergedRows] = useState<OcrExtractedRow[]>([]);
//   const [detectedParty, setDetectedParty] = useState('');
//   const [detectedCategory, setDetectedCategory] = useState('');
//   const [detectedDate, setDetectedDate] = useState('');
//   const [submitting, setSubmitting] = useState(false);
//   const [currentPreview, setCurrentPreview] = useState<OcrFile | null>(null);
//   const [editingRowId, setEditingRowId] = useState<string | null>(null);
//   const [scanProgress, setScanProgress] = useState(0);
//   const [scanStatus, setScanStatus] = useState('');
//   const fileInputRef = useRef<HTMLInputElement>(null);

//   // Possible categories from selected party
//   const partyCategories = React.useMemo(() => {
//     if (!selectedPartyId) return [];
//     const p = partiesList.find(p => String(p.id) === String(selectedPartyId));
//     return p?.categories || [];
//   }, [selectedPartyId, partiesList]);

//   // Stock name suggestions for autocomplete
//   const isMutual = selectedCategory?.toLowerCase().includes('mutual');
//   const stockNameOptions = (isMutual ? mutualsnames : stocksnames).map(s => ({ value: s.name, label: s.name }));

//   // ── File drop/pick ──────────────────────────────────────────────────────────

//   const addFiles = useCallback((incoming: File[]) => {
//     const valid = incoming.filter(f => SUPPORTED_TYPES.includes(f.type));
//     if (valid.length !== incoming.length) alert('Some files skipped. Supported: JPG, PNG, WEBP, GIF, PDF');
//     const newFiles: OcrFile[] = valid.map(f => {
//       const isImg = f.type.startsWith('image/');
//       return {
//         file: f, id: uid(), status: 'pending',
//         preview: isImg ? URL.createObjectURL(f) : undefined,
//       };
//     });
//     setFiles(prev => [...prev, ...newFiles]);
//   }, []);

//   const onDrop = useCallback((e: React.DragEvent) => {
//     e.preventDefault(); setDragging(false);
//     addFiles(Array.from(e.dataTransfer.files));
//   }, [addFiles]);

//   const onFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
//     addFiles(Array.from(e.target.files || []));
//     e.target.value = '';
//   };

//   // ── Run OCR ────────────────────────────────────────────────────────────────

//   const handleScan = async () => {
//     if (!files.length) { alert('Upload at least one file'); return; }
//     setStep(1);
//     setScanProgress(0);

//     const total = files.length;
//     let doneCount = 0;
//     const allRows: OcrExtractedRow[] = [];
//     let firstParty = '', firstCategory = '', firstDate = '';

//     const updatedFiles = [...files];

//     for (let i = 0; i < files.length; i++) {
//       const f = files[i];
//       setScanStatus(`Scanning ${f.file.name}…`);
//       updatedFiles[i] = { ...f, status: 'processing' };
//       setFiles([...updatedFiles]);

//       try {
//         const result = await runOcrOnFile(f.file);
//         updatedFiles[i] = { ...updatedFiles[i], status: 'done', result };
//         setFiles([...updatedFiles]);

//         allRows.push(...result.rows);
//         if (!firstParty && result.party_name) firstParty = result.party_name;
//         if (!firstCategory && result.category) firstCategory = result.category;
//         if (!firstDate && result.date) firstDate = result.date;

//         doneCount++;
//         setScanProgress(Math.round((doneCount / total) * 100));
//       } catch (err: any) {
//         updatedFiles[i] = { ...updatedFiles[i], status: 'error', error: err.message };
//         setFiles([...updatedFiles]);
//         doneCount++;
//         setScanProgress(Math.round((doneCount / total) * 100));
//       }
//     }

//     // Dedup rows by stock_name (merge amounts)
//     const dedupMap = new Map<string, OcrExtractedRow>();
//     for (const row of allRows) {
//       const key = row.stock_name?.toLowerCase().trim();
//       if (!key) continue;
//       if (dedupMap.has(key)) {
//         const existing = dedupMap.get(key)!;
//         const newUnit = safeNum(existing.unit) + safeNum(row.unit);
//         const newTotal = safeNum(existing.total_amount) + safeNum(row.total_amount);
//         const newIndirect = safeNum(existing.indirect_charges) + safeNum(row.indirect_charges);
//         dedupMap.set(key, {
//           ...existing,
//           unit: newUnit.toString(),
//           total_amount: newTotal.toString(),
//           indirect_charges: newIndirect.toString(),
//           per_unit_charge: newUnit > 0 ? ((newTotal + newIndirect) / newUnit).toFixed(2) : '0',
//         });
//       } else {
//         dedupMap.set(key, { ...row });
//       }
//     }

//     setMergedRows([...dedupMap.values()]);
//     setDetectedParty(firstParty);
//     setDetectedCategory(firstCategory);
//     setDetectedDate(firstDate);
//     if (firstDate) setOverrideDate(firstDate);

//     // Auto-match party
//     if (firstParty) {
//       const match = partiesList.find(p => p.name.toLowerCase().includes(firstParty.toLowerCase()) || firstParty.toLowerCase().includes(p.name.toLowerCase()));
//       if (match) setSelectedPartyId(String(match.id));
//     }
//     // Auto-match category
//     if (firstCategory) setSelectedCategory(firstCategory);

//     setScanStatus('');
//     setStep(2);
//   };

//   // ── Row editing ────────────────────────────────────────────────────────────

//   const updateRow = (id: string, patch: Partial<OcrExtractedRow>) => {
//     setMergedRows(prev => prev.map(r => {
//       if (r.id !== id) return r;
//       const updated = { ...r, ...patch, edited: true };
//       // Recalculate per unit when amounts change
//       if ('unit' in patch || 'total_amount' in patch || 'indirect_charges' in patch) {
//         const u = safeNum(updated.unit);
//         const t = safeNum(updated.total_amount);
//         const ind = safeNum(updated.indirect_charges);
//         updated.per_unit_charge = u > 0 ? ((t + ind) / u).toFixed(2) : '0';
//       }
//       return updated;
//     }));
//   };

//   const deleteRow = (id: string) => setMergedRows(prev => prev.filter(r => r.id !== id));

//   const addManualRow = () => {
//     setMergedRows(prev => [...prev, {
//       id: uid(), stock_name: '', unit: '', total_amount: '',
//       indirect_charges: '0', per_unit_charge: '', confidence: 'high', edited: true,
//     }]);
//   };

//   // ── Submit ─────────────────────────────────────────────────────────────────

//   const handleSubmit = async () => {
//     if (!selectedPartyId) { alert('Select a party'); return; }
//     if (!selectedCategory) { alert('Select a category'); return; }
//     const date = overrideDate || detectedDate;
//     if (!date) { alert('Select a purchase date'); return; }
//     const activeRows = mergedRows.filter(r => !r.deleted && r.stock_name?.trim());
//     if (!activeRows.length) { alert('No valid rows to submit'); return; }
//     setSubmitting(true);
//     try {
//       await onSubmit({ partyId: selectedPartyId, category: selectedCategory, date, rows: activeRows });
//       setStep(3);
//     } catch (err: any) {
//       alert('Submit error: ' + err.message);
//     } finally {
//       setSubmitting(false);
//     }
//   };

//   // ── Grand totals ───────────────────────────────────────────────────────────

//   const grandTotal = mergedRows
//     .filter(r => !r.deleted)
//     .reduce((sum, r) => sum + safeNum(r.total_amount) + safeNum(r.indirect_charges), 0);

//   // ─────────────────────────────────────────────────────────────────────────────
//   // RENDER
//   // ─────────────────────────────────────────────────────────────────────────────

//   const modalStyle: React.CSSProperties = {
//     position: 'fixed', inset: 0, zIndex: 50,
//     display: 'flex', alignItems: 'center', justifyContent: 'center',
//     background: 'rgba(0,0,0,0.55)',
//     backdropFilter: 'blur(6px)',
//     padding: '1rem',
//   };

//   const cardStyle: React.CSSProperties = {
//     background: '#fff',
//     borderRadius: 20,
//     boxShadow: '0 25px 60px rgba(0,0,0,0.2)',
//     width: '100%',
//     maxWidth: step === 2 ? 1060 : 600,
//     maxHeight: '92vh',
//     display: 'flex',
//     flexDirection: 'column',
//     overflow: 'hidden',
//     transition: 'max-width 0.4s',
//   };

//   // ── HEADER ────────────────────────────────────────────────────────────────

//   const Header = () => (
//     <div style={{
//       background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 50%, #4338CA 100%)',
//       padding: '20px 24px 16px',
//       flexShrink: 0,
//     }}>
//       <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
//         <div>
//           <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
//             <div style={{ background: 'rgba(255,255,255,0.15)', borderRadius: 10, padding: '6px 10px', fontSize: 20 }}>🔍</div>
//             <div>
//               <h2 style={{ color: '#fff', fontSize: 18, fontWeight: 700, margin: 0 }}>AI Document Scanner</h2>
//               <p style={{ color: '#a5b4fc', fontSize: 12, margin: 0, marginTop: 2 }}>
//                 Claude Vision OCR · Auto-extract stocks from any document
//               </p>
//             </div>
//           </div>
//           <StepBar step={step} />
//         </div>
//         <button
//           onClick={onClose}
//           style={{ background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: 8, color: '#fff', padding: '6px 10px', cursor: 'pointer', fontSize: 18, lineHeight: 1 }}
//         >
//           ×
//         </button>
//       </div>
//     </div>
//   );

//   // ── STEP 0: UPLOAD ────────────────────────────────────────────────────────

//   if (step === 0) return (
//     <div style={modalStyle}>
//       <div style={cardStyle}>
//         <Header />
//         <div style={{ overflowY: 'auto', flex: 1, padding: 24 }}>

//           {/* Drop zone */}
//           <div
//             onDragOver={e => { e.preventDefault(); setDragging(true); }}
//             onDragLeave={() => setDragging(false)}
//             onDrop={onDrop}
//             onClick={() => fileInputRef.current?.click()}
//             style={{
//               border: `2px dashed ${dragging ? '#6366F1' : '#C7D2FE'}`,
//               borderRadius: 16, padding: 40, textAlign: 'center',
//               cursor: 'pointer', transition: 'all 0.2s',
//               background: dragging ? '#EEF2FF' : '#F5F7FF',
//             }}
//           >
//             <div style={{ fontSize: 48, marginBottom: 12 }}>📄</div>
//             <p style={{ fontSize: 16, fontWeight: 600, color: '#3730A3', margin: 0 }}>
//               Drop documents here or click to browse
//             </p>
//             <p style={{ fontSize: 13, color: '#6B7280', marginTop: 8 }}>
//               Contract notes · Broker statements · Portfolio PDFs · Screenshots · Scanned images
//             </p>
//             <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
//               {['PDF', 'JPG', 'PNG', 'WEBP'].map(ext => (
//                 <span key={ext} style={{ background: '#E0E7FF', color: '#3730A3', borderRadius: 6, padding: '2px 10px', fontSize: 12, fontWeight: 600 }}>{ext}</span>
//               ))}
//             </div>
//           </div>
//           <input ref={fileInputRef} type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.webp,.gif" style={{ display: 'none' }} onChange={onFileInput} />

//           {/* File list */}
//           {files.length > 0 && (
//             <div style={{ marginTop: 20 }}>
//               <p style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 10 }}>
//                 {files.length} file{files.length !== 1 ? 's' : ''} ready
//               </p>
//               <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
//                 {files.map((f, i) => (
//                   <div key={f.id} style={{
//                     display: 'flex', alignItems: 'center', gap: 12,
//                     background: '#F9FAFB', border: '1px solid #E5E7EB',
//                     borderRadius: 12, padding: '10px 14px',
//                   }}>
//                     {f.preview
//                       ? <img src={f.preview} alt="" style={{ width: 44, height: 44, borderRadius: 8, objectFit: 'cover' }} />
//                       : <div style={{ width: 44, height: 44, borderRadius: 8, background: '#EEF2FF', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>📋</div>
//                     }
//                     <div style={{ flex: 1, minWidth: 0 }}>
//                       <p style={{ margin: 0, fontSize: 13, fontWeight: 500, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.file.name}</p>
//                       <p style={{ margin: 0, fontSize: 12, color: '#6B7280' }}>{(f.file.size / 1024).toFixed(1)} KB · {f.file.type.split('/')[1].toUpperCase()}</p>
//                     </div>
//                     <button onClick={() => setFiles(prev => prev.filter((_, ii) => ii !== i))} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: '#9CA3AF', padding: 4 }}>×</button>
//                   </div>
//                 ))}
//               </div>
//             </div>
//           )}

//           {/* Feature callouts */}
//           <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginTop: 24 }}>
//             {[
//               { icon: '🤖', title: 'AI-powered', desc: 'Claude Vision reads any document format' },
//               { icon: '📊', title: 'Auto-extract', desc: 'Stocks, amounts, dates pulled automatically' },
//               { icon: '✏️', title: 'Review & fix', desc: 'Edit, add, or delete rows before importing' },
//             ].map(item => (
//               <div key={item.title} style={{ background: '#F9FAFB', borderRadius: 12, padding: '14px 12px', border: '1px solid #F3F4F6' }}>
//                 <div style={{ fontSize: 24, marginBottom: 6 }}>{item.icon}</div>
//                 <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: '#111827' }}>{item.title}</p>
//                 <p style={{ margin: 0, fontSize: 12, color: '#6B7280', marginTop: 3 }}>{item.desc}</p>
//               </div>
//             ))}
//           </div>
//         </div>

//         <div style={{ padding: '16px 24px', borderTop: '1px solid #F3F4F6', background: '#FAFAFA', flexShrink: 0 }}>
//           <button
//             onClick={handleScan}
//             disabled={!files.length}
//             style={{
//               width: '100%', padding: '13px 0', borderRadius: 12, border: 'none', cursor: files.length ? 'pointer' : 'not-allowed',
//               background: files.length ? 'linear-gradient(135deg, #6366F1, #4F46E5)' : '#E5E7EB',
//               color: files.length ? '#fff' : '#9CA3AF', fontSize: 15, fontWeight: 600,
//               display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
//             }}
//           >
//             <span>🔍</span> Scan with AI OCR →
//           </button>
//         </div>
//       </div>
//     </div>
//   );

//   // ── STEP 1: SCANNING ──────────────────────────────────────────────────────

//   if (step === 1) return (
//     <div style={modalStyle}>
//       <div style={{ ...cardStyle, maxWidth: 500 }}>
//         <Header />
//         <div style={{ padding: 40, textAlign: 'center', flex: 1 }}>
//           <div style={{ fontSize: 64, marginBottom: 20 }}>
//             {scanProgress < 100 ? '🔍' : '✅'}
//           </div>
//           <h3 style={{ fontSize: 20, fontWeight: 700, color: '#1F2937', margin: 0, marginBottom: 8 }}>
//             {scanProgress < 100 ? 'AI is reading your documents…' : 'Scan complete!'}
//           </h3>
//           <p style={{ fontSize: 14, color: '#6B7280', marginBottom: 28 }}>{scanStatus || 'Processing…'}</p>

//           {/* Progress bar */}
//           <div style={{ background: '#E5E7EB', borderRadius: 99, height: 10, overflow: 'hidden', marginBottom: 12 }}>
//             <div style={{
//               background: 'linear-gradient(90deg, #6366F1, #818CF8)',
//               height: '100%', width: `${scanProgress}%`,
//               transition: 'width 0.4s ease', borderRadius: 99,
//             }} />
//           </div>
//           <p style={{ fontSize: 13, color: '#6366F1', fontWeight: 600 }}>{scanProgress}%</p>

//           {/* Per-file status */}
//           <div style={{ marginTop: 24, textAlign: 'left' }}>
//             {files.map(f => (
//               <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid #F3F4F6' }}>
//                 <span style={{ fontSize: 16 }}>
//                   {f.status === 'done' ? '✅' : f.status === 'error' ? '❌' : f.status === 'processing' ? '⏳' : '⬜'}
//                 </span>
//                 <span style={{ fontSize: 13, color: '#374151', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.file.name}</span>
//                 {f.status === 'done' && f.result && (
//                   <span style={{ fontSize: 12, color: '#10B981', fontWeight: 500 }}>{f.result.rows.length} rows</span>
//                 )}
//                 {f.status === 'error' && (
//                   <span style={{ fontSize: 12, color: '#EF4444' }}>Failed</span>
//                 )}
//               </div>
//             ))}
//           </div>
//         </div>
//       </div>
//     </div>
//   );

//   // ── STEP 2: REVIEW ────────────────────────────────────────────────────────

//   if (step === 2) {
//     const activeRows = mergedRows.filter(r => !r.deleted);

//     return (
//       <div style={modalStyle}>
//         <div style={cardStyle}>
//           <Header />
//           <div style={{ overflowY: 'auto', flex: 1 }}>

//             {/* Top config bar */}
//             <div style={{ padding: '16px 24px', background: '#F8F9FF', borderBottom: '1px solid #E5E7EB', display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>

//               {/* Detection status */}
//               {(detectedParty || detectedCategory || detectedDate) && (
//                 <div style={{ width: '100%', display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
//                   <span style={{ fontSize: 12, color: '#6B7280', fontWeight: 500 }}>AI detected:</span>
//                   {detectedParty && <span style={{ background: '#ECFDF5', color: '#065F46', borderRadius: 6, padding: '2px 10px', fontSize: 12, fontWeight: 500 }}>Party: {detectedParty}</span>}
//                   {detectedCategory && <span style={{ background: '#EEF2FF', color: '#3730A3', borderRadius: 6, padding: '2px 10px', fontSize: 12, fontWeight: 500 }}>Category: {detectedCategory}</span>}
//                   {detectedDate && <span style={{ background: '#FFF7ED', color: '#9A3412', borderRadius: 6, padding: '2px 10px', fontSize: 12, fontWeight: 500 }}>Date: {detectedDate}</span>}
//                 </div>
//               )}

//               {/* Party */}
//               <div style={{ flex: 1, minWidth: 180 }}>
//                 <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>
//                   Party <span style={{ color: '#EF4444' }}>*</span>
//                 </label>
//                 <select
//                   value={selectedPartyId}
//                   onChange={e => { setSelectedPartyId(e.target.value); setSelectedCategory(''); }}
//                   style={{ width: '100%', border: '1px solid #D1D5DB', borderRadius: 8, padding: '7px 10px', fontSize: 13, outline: 'none', color: '#111827' }}
//                 >
//                   <option value="">Choose party</option>
//                   {partiesList.map(p => <option key={p.id} value={String(p.id)}>{p.name}</option>)}
//                 </select>
//               </div>

//               {/* Category */}
//               <div style={{ flex: 1, minWidth: 160 }}>
//                 <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>
//                   Category <span style={{ color: '#EF4444' }}>*</span>
//                 </label>
//                 <select
//                   value={selectedCategory}
//                   onChange={e => setSelectedCategory(e.target.value)}
//                   disabled={!selectedPartyId}
//                   style={{ width: '100%', border: '1px solid #D1D5DB', borderRadius: 8, padding: '7px 10px', fontSize: 13, outline: 'none', color: '#111827', background: !selectedPartyId ? '#F9FAFB' : '#fff' }}
//                 >
//                   <option value="">Select category</option>
//                   {partyCategories.map(c => <option key={c} value={c}>{c}</option>)}
//                 </select>
//               </div>

//               {/* Date */}
//               <div style={{ flex: 1, minWidth: 150 }}>
//                 <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>
//                   Purchase Date <span style={{ color: '#EF4444' }}>*</span>
//                 </label>
//                 <input
//                   type="date"
//                   value={overrideDate}
//                   onChange={e => setOverrideDate(e.target.value)}
//                   style={{ width: '100%', border: '1px solid #D1D5DB', borderRadius: 8, padding: '7px 10px', fontSize: 13, outline: 'none', boxSizing: 'border-box' }}
//                 />
//               </div>
//             </div>

//             {/* Stats bar */}
//             <div style={{ display: 'flex', gap: 12, padding: '12px 24px', background: '#fff', borderBottom: '1px solid #F3F4F6', flexWrap: 'wrap' }}>
//               {[
//                 { label: 'Total rows', value: activeRows.length, color: '#3730A3' },
//                 { label: 'High confidence', value: activeRows.filter(r => r.confidence === 'high').length, color: '#065F46' },
//                 { label: 'Needs review', value: activeRows.filter(r => r.confidence !== 'high').length, color: '#B45309' },
//                 { label: 'Grand total', value: `₹${grandTotal.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`, color: '#1F2937' },
//               ].map(s => (
//                 <div key={s.label} style={{ background: '#F9FAFB', borderRadius: 10, padding: '8px 16px', border: '1px solid #F3F4F6' }}>
//                   <p style={{ margin: 0, fontSize: 11, color: '#6B7280' }}>{s.label}</p>
//                   <p style={{ margin: 0, fontSize: 16, fontWeight: 700, color: s.color }}>{s.value}</p>
//                 </div>
//               ))}
//             </div>

//             {/* Table */}
//             <div style={{ padding: '0 0 24px' }}>
//               <div style={{ overflowX: 'auto' }}>
//                 <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
//                   <thead>
//                     <tr style={{ background: '#F9FAFB', borderBottom: '1px solid #E5E7EB' }}>
//                       <th style={{ textAlign: 'left', padding: '10px 16px', fontWeight: 600, color: '#374151', width: 28 }}>#</th>
//                       <th style={{ textAlign: 'left', padding: '10px 8px', fontWeight: 600, color: '#374151', minWidth: 200 }}>Stock / Fund Name</th>
//                       <th style={{ textAlign: 'right', padding: '10px 8px', fontWeight: 600, color: '#374151', minWidth: 80 }}>Unit</th>
//                       <th style={{ textAlign: 'right', padding: '10px 8px', fontWeight: 600, color: '#374151', minWidth: 110 }}>Total (₹)</th>
//                       <th style={{ textAlign: 'right', padding: '10px 8px', fontWeight: 600, color: '#374151', minWidth: 100 }}>Charges (₹)</th>
//                       <th style={{ textAlign: 'right', padding: '10px 8px', fontWeight: 600, color: '#374151', minWidth: 90 }}>Per Unit</th>
//                       <th style={{ textAlign: 'center', padding: '10px 8px', fontWeight: 600, color: '#374151', width: 90 }}>Confidence</th>
//                       <th style={{ textAlign: 'center', padding: '10px 8px', fontWeight: 600, color: '#374151', width: 70 }}>Action</th>
//                     </tr>
//                   </thead>
//                   <tbody>
//                     {activeRows.map((row, ri) => {
//                       const isEditing = editingRowId === row.id;
//                       return (
//                         <tr key={row.id}
//                           style={{
//                             borderBottom: '1px solid #F3F4F6',
//                             background: row.confidence === 'low' ? '#FFFBEB' : row.edited ? '#F0FDF4' : '#fff',
//                           }}
//                         >
//                           <td style={{ padding: '8px 16px', color: '#9CA3AF' }}>{ri + 1}</td>

//                           {/* Stock Name */}
//                           <td style={{ padding: '8px 8px' }}>
//                             {isEditing ? (
//                               <Select
//                                 options={stockNameOptions}
//                                 value={row.stock_name ? { value: row.stock_name, label: row.stock_name } : null}
//                                 onChange={sel => updateRow(row.id, { stock_name: sel?.value || '' })}
//                                 onInputChange={input => input && updateRow(row.id, { stock_name: input })}
//                                 placeholder="Search or type name…"
//                                 menuPortalTarget={document.body}
//                                 styles={{
//                                   control: b => ({ ...b, fontSize: 13, minHeight: 32, borderRadius: 6, borderColor: '#6366F1' }),
//                                   menu: b => ({ ...b, zIndex: 9999 }),
//                                 }}
//                               />
//                             ) : (
//                               <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
//                                 <span style={{ fontWeight: 500, color: '#111827' }}>{row.stock_name || <em style={{ color: '#9CA3AF' }}>—</em>}</span>
//                                 {row.edited && <span title="Edited" style={{ fontSize: 10, color: '#10B981' }}>✎</span>}
//                               </div>
//                             )}
//                           </td>

//                           {/* Unit */}
//                           <td style={{ padding: '8px 8px', textAlign: 'right' }}>
//                             {isEditing
//                               ? <input type="number" value={row.unit} onChange={e => updateRow(row.id, { unit: e.target.value })} style={{ width: 72, border: '1px solid #6366F1', borderRadius: 6, padding: '4px 6px', fontSize: 13, textAlign: 'right' }} />
//                               : <span style={{ color: '#374151' }}>{row.unit || '—'}</span>
//                             }
//                           </td>

//                           {/* Total */}
//                           <td style={{ padding: '8px 8px', textAlign: 'right' }}>
//                             {isEditing
//                               ? <input type="number" value={row.total_amount} onChange={e => updateRow(row.id, { total_amount: e.target.value })} style={{ width: 100, border: '1px solid #6366F1', borderRadius: 6, padding: '4px 6px', fontSize: 13, textAlign: 'right' }} />
//                               : <span style={{ color: '#111827', fontWeight: 500 }}>₹{safeNum(row.total_amount).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>
//                             }
//                           </td>

//                           {/* Indirect Charges */}
//                           <td style={{ padding: '8px 8px', textAlign: 'right' }}>
//                             {isEditing
//                               ? <input type="number" value={row.indirect_charges} onChange={e => updateRow(row.id, { indirect_charges: e.target.value })} style={{ width: 88, border: '1px solid #6366F1', borderRadius: 6, padding: '4px 6px', fontSize: 13, textAlign: 'right' }} />
//                               : <span style={{ color: '#6B7280' }}>₹{safeNum(row.indirect_charges).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>
//                             }
//                           </td>

//                           {/* Per unit */}
//                           <td style={{ padding: '8px 8px', textAlign: 'right' }}>
//                             <span style={{ color: '#6B7280', fontSize: 12 }}>{row.per_unit_charge ? `₹${safeNum(row.per_unit_charge).toFixed(2)}` : '—'}</span>
//                           </td>

//                           {/* Confidence */}
//                           <td style={{ padding: '8px 8px', textAlign: 'center' }}>
//                             <ConfidenceBadge level={row.confidence} />
//                           </td>

//                           {/* Actions */}
//                           <td style={{ padding: '8px 8px', textAlign: 'center' }}>
//                             <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
//                               <button
//                                 title={isEditing ? 'Done' : 'Edit'}
//                                 onClick={() => setEditingRowId(isEditing ? null : row.id)}
//                                 style={{ background: isEditing ? '#ECFDF5' : '#EEF2FF', border: 'none', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', color: isEditing ? '#065F46' : '#3730A3', fontSize: 13 }}
//                               >
//                                 {isEditing ? '✓' : '✏'}
//                               </button>
//                               <button
//                                 title="Delete row"
//                                 onClick={() => deleteRow(row.id)}
//                                 style={{ background: '#FEF2F2', border: 'none', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', color: '#991B1B', fontSize: 13 }}
//                               >
//                                 ✕
//                               </button>
//                             </div>
//                           </td>
//                         </tr>
//                       );
//                     })}
//                   </tbody>
//                 </table>
//               </div>

//               {/* Add row */}
//               <div style={{ padding: '12px 24px' }}>
//                 <button
//                   onClick={addManualRow}
//                   style={{ background: 'none', border: '1.5px dashed #C7D2FE', borderRadius: 10, padding: '8px 20px', color: '#6366F1', fontSize: 13, fontWeight: 600, cursor: 'pointer', width: '100%' }}
//                 >
//                   + Add row manually
//                 </button>
//               </div>
//             </div>
//           </div>

//           {/* Footer */}
//           <div style={{ padding: '14px 24px', borderTop: '1px solid #F3F4F6', background: '#FAFAFA', flexShrink: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
//             <button
//               onClick={() => setStep(0)}
//               style={{ background: 'none', border: '1px solid #D1D5DB', borderRadius: 10, padding: '9px 20px', color: '#374151', fontSize: 14, fontWeight: 500, cursor: 'pointer' }}
//             >
//               ← Re-scan
//             </button>
//             <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
//               <span style={{ fontSize: 13, color: '#6B7280' }}>
//                 {activeRows.length} rows · <strong style={{ color: '#111827' }}>₹{grandTotal.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</strong>
//               </span>
//               <button
//                 onClick={handleSubmit}
//                 disabled={submitting || !selectedPartyId || !selectedCategory || !overrideDate}
//                 style={{
//                   background: submitting || !selectedPartyId || !selectedCategory || !overrideDate
//                     ? '#E5E7EB' : 'linear-gradient(135deg, #10B981, #059669)',
//                   border: 'none', borderRadius: 10, padding: '9px 28px',
//                   color: submitting || !selectedPartyId || !selectedCategory || !overrideDate ? '#9CA3AF' : '#fff',
//                   fontSize: 14, fontWeight: 600, cursor: 'pointer',
//                 }}
//               >
//                 {submitting ? 'Importing…' : `Import ${activeRows.length} rows →`}
//               </button>
//             </div>
//           </div>
//         </div>
//       </div>
//     );
//   }

//   // ── STEP 3: SUCCESS ───────────────────────────────────────────────────────

//   return (
//     <div style={modalStyle}>
//       <div style={{ ...cardStyle, maxWidth: 460, textAlign: 'center' }}>
//         <Header />
//         <div style={{ padding: 48, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
//           <div style={{ fontSize: 64 }}>🎉</div>
//           <h3 style={{ fontSize: 22, fontWeight: 700, color: '#111827', margin: 0 }}>Import successful!</h3>
//           <p style={{ fontSize: 14, color: '#6B7280', margin: 0 }}>
//             {mergedRows.filter(r => !r.deleted).length} stock rows have been imported.
//           </p>
//           <div style={{ background: '#ECFDF5', border: '1px solid #A7F3D0', borderRadius: 12, padding: '14px 20px', marginTop: 8 }}>
//             <p style={{ margin: 0, fontSize: 13, color: '#065F46' }}>
//               Total amount: <strong>₹{grandTotal.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</strong>
//             </p>
//           </div>
//           <button onClick={onClose} style={{ background: 'linear-gradient(135deg, #6366F1, #4F46E5)', border: 'none', borderRadius: 10, padding: '11px 32px', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer', marginTop: 8 }}>
//             Done
//           </button>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default OcrUploadModal;

// // ─────────────────────────────────────────────────────────────────────────────
// // INTEGRATION SNIPPET — paste into Addstock.tsx
// // ─────────────────────────────────────────────────────────────────────────────
// /*

// 1. Import:
//    import OcrUploadModal from './OcrUploadModal';

// 2. Add state:
//    const [showOcrModal, setShowOcrModal] = useState(false);

// 3. Replace the Upload Excel button:
//    <button type="button" onClick={() => setShowOcrModal(true)} className="...">
//      <svg .../>  Scan Document
//    </button>

// 4. Add handler (mirrors handleExcelSubmit logic):
//    const handleOcrSubmit = async ({ partyId, category, date, rows }) => {
//      for (const row of rows) {
//        const existing = stocksDataDb.find(r => {
//          if (String(r.party_id) !== String(partyId)) return false;
//          const items = typeof r.categories === 'string' ? JSON.parse(r.categories) : r.categories;
//          return items.some(i => i.stock_name === row.stock_name && i.category === category);
//        });
//        // PUT if existing, POST if new — same pattern as handleExcelSubmit
//        // ... (see handleExcelSubmit in Addstock.tsx)
//      }
//      await fetchParties();
//      setShowOcrModal(false);
//    };

// 5. Render modal at bottom of return:
//    {showOcrModal && (
//      <OcrUploadModal
//        partiesList={partiesList}
//        stocksnames={stocksnames}
//        mutualsnames={mutualsnames}
//        onSubmit={handleOcrSubmit}
//        onClose={() => setShowOcrModal(false)}
//        apiUrl={process.env.REACT_APP_API_URL || ''}
//      />
//    )}
// */
import React, { useCallback, useRef, useState } from 'react';
import Select from 'react-select';
import Tesseract from 'tesseract.js';

// ─────────────────────────────────────────────────────────────────────────────
// TYPES
// ─────────────────────────────────────────────────────────────────────────────

interface Party {
  id: number | string;
  name: string;
  categories?: string[];
  total_amount?: number | string;
}

interface OcrExtractedRow {
  id: string;
  stock_name: string;
  unit: string;
  total_amount: string;
  indirect_charges: string;
  per_unit_charge: string;
  confidence: 'high' | 'medium' | 'low';
  raw_text?: string;
  edited?: boolean;
  deleted?: boolean;
  row_date?: string;
}

interface OcrInvoice {
  invoice_no?: string;
  date?: string;
  rows: OcrExtractedRow[];
}

interface OcrResult {
  party_name?: string;
  category?: string;
  doc_type?: string;
  confidence_overall?: number;
  invoices: OcrInvoice[];
  date?: string;
  rows?: OcrExtractedRow[];
}

interface OcrFile {
  file: File;
  id: string;
  preview?: string;
  status: 'pending' | 'processing' | 'done' | 'error';
  result?: OcrResult;
  error?: string;
  extractedText?: string;
}

interface InvoiceBatch {
  id: string;
  date: string;
  invoice_no?: string;
  rows: OcrExtractedRow[];
  expanded: boolean;
}

interface Props {
  partiesList: Party[];
  stocksnames: { id: number; name: string }[];
  mutualsnames: { id: number; name: string }[];
  onSubmit: (payload: {
    partyId: string;
    category: string;
    batches: { date: string; rows: OcrExtractedRow[] }[];
  }) => Promise<void>;
  onClose: () => void;
  apiUrl: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────────────────

const safeNum = (v: any) => {
  const n = parseFloat(v);
  return isNaN(n) ? 0 : n;
};

const uid = () => Math.random().toString(36).slice(2, 9);

const SUPPORTED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'application/pdf'];

// ─────────────────────────────────────────────────────────────────────────────
// STEP 1: Extract raw text using Tesseract.js
// Works for images. For PDFs, we convert pages to images first via canvas.
// ─────────────────────────────────────────────────────────────────────────────

// NOTE: Patch for PDF.js workerSrc: Use 'pdfjs-dist/build/pdf.worker.min.js' from public folder instead of CDN to avoid CORS/fetch failures.
// Steps to enable worker:
//   1. Install pdfjs-dist in your project (`npm install pdfjs-dist`)
//   2. Copy the worker to public: 
//        cp node_modules/pdfjs-dist/build/pdf.worker.min.js public/pdf.worker.min.js
//   3. The code below will set workerSrc accordingly.

async function extractTextWithTesseract(
  file: File,
  onProgress: (pct: number, status: string) => void
): Promise<string> {
  if (file.type === 'application/pdf') {
    // Dynamically load pdfjs-dist (add to package.json: pdfjs-dist)
    const pdfjsLib = await import('pdfjs-dist');
    // Use local worker, not CDN (to avoid CORS/fetch errors)
    // CRA and Vite both serve public/ as root, so this will work for both
    // pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL ? process.env.PUBLIC_URL : ''}/pdf.worker.min.js`;
    pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.DOCS_URL || ''}/pdf.worker.min.mjs`;


    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
    const totalPages = pdf.numPages;
    let fullText = '';

    for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
      onProgress(
        Math.round(((pageNum - 1) / totalPages) * 60),
        `Reading page ${pageNum} of ${totalPages}…`
      );

      const page = await pdf.getPage(pageNum);
      const viewport = page.getViewport({ scale: 2.0 }); // scale=2 for better OCR quality
      const canvas = document.createElement('canvas');
      canvas.width = viewport.width;
      canvas.height = viewport.height;
    //   const ctx = canvas.getContext('2d')!;
    //   await page.render({ canvasContext: ctx, viewport, canvas }).promise;
      const ctx = canvas.getContext('2d')!;
      await page.render({ canvasContext: ctx, viewport, canvas }).promise;
 



      const imageDataUrl = canvas.toDataURL('image/png');

      const result = await Tesseract.recognize(imageDataUrl, 'eng', {
        logger: () => {}, // suppress per-character logs
      });

      fullText += `\n\n--- PAGE ${pageNum} ---\n${result.data.text}`;
    }

    return fullText.trim();
  } else {
    // Image file — direct Tesseract
    onProgress(10, 'Running OCR on image…');
    const result = await Tesseract.recognize(file, 'eng', {
      logger: (m: any) => {
        if (m.status === 'recognizing text') {
          onProgress(10 + Math.round(m.progress * 50), 'Recognizing text…');
        }
      },
    });
    return result.data.text.trim();
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// STEP 2: Send extracted TEXT to Claude to parse into structured JSON
// Much cheaper than sending full PDF/image to Claude
// ─────────────────────────────────────────────────────────────────────────────

const buildParsePrompt = (text: string) => `
You are an expert financial document parser. Below is raw OCR text extracted from mutual fund / stock purchase invoices. The text may have OCR errors, garbled spacing, or noise.

Parse it into structured JSON. The document may contain MULTIPLE invoices on separate pages — each with a different date. Create one invoice object per distinct date/invoice.

Return ONLY valid JSON, no markdown, no extra text:
{
  "party_name": "client/consignee name if found, else null",
  "category": "Equity / Mutual Funds / FNO / ETF / else null",
  "doc_type": "e.g. mutual fund invoice, contract note",
  "confidence_overall": <0-100>,
  "invoices": [
    {
      "invoice_no": "invoice number if visible, else null",
      "date": "YYYY-MM-DD",
      "rows": [
        {
          "stock_name": "fund/stock name",
          "unit": "quantity as plain number string",
          "total_amount": "rupee amount as plain number string, no commas, no ₹",
          "indirect_charges": "charges/brokerage as number string, 0 if none",
          "per_unit_charge": "NAV/price per unit as number string",
          "confidence": "high|medium|low",
          "raw_text": "original text this was extracted from"
        }
      ]
    }
  ]
}

RULES:
- One invoice object per unique date — do NOT merge different dates
- Fix obvious OCR errors (e.g. "1OO" → "100", "O" → "0")
- Strip commas from numbers: 1,00,000 → 100000
- If per_unit_charge missing, compute: total_amount / unit
- confidence = high if clear, medium if inferred, low if guessed
- NEVER output text before or after the JSON

RAW OCR TEXT:
${text}
`;

// ── Repair truncated JSON ──────────────────────────────────────────────────
function repairTruncatedJson(str: string): string {
    let s = str.trim();
    s = s.replace(/,?\s*"[^"]*$/, '');  // drop trailing partial string
    s = s.replace(/,\s*$/, '');          // drop trailing comma
  
    const stack: string[] = [];
    let inString = false, escape = false;
    for (const ch of s) {
      if (escape) { escape = false; continue; }
      if (ch === '\\' && inString) { escape = true; continue; }
      if (ch === '"') { inString = !inString; continue; }
      if (inString) continue;
      if (ch === '{') stack.push('}');
      else if (ch === '[') stack.push(']');
      else if (ch === '}' || ch === ']') stack.pop();
    }
    return s + stack.reverse().join('');
  }
  
  // ── Continue prompt for truncated responses ────────────────────────────────
  const buildContinuePrompt = (text: string, partialJson: string) => `
  You are continuing a JSON parsing task that was cut off due to token limits.
  
  Here is the PARTIAL JSON that was already generated (do NOT repeat it):
  ${partialJson}
  
  Here is the original OCR text being parsed:
  ${text}
  
  Continue from where the JSON was cut off. Output ONLY the remaining JSON needed to 
  complete the structure — starting from exactly where the cut happened.
  Do NOT output the full JSON again. Do NOT add markdown. Just the continuation fragment.
  `;
  
  // ── Core Gemini call ───────────────────────────────────────────────────────
  const GEMINI_URL =
    `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash:generateContent?key=YOUR_KEY_HERE`;
  
  async function callGemini(prompt: string): Promise<{ text: string; truncated: boolean }> {
    const response = await fetch(GEMINI_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        contents: [{ parts: [{ text: prompt }] }],
        generationConfig: {
          temperature: 0.1,
          maxOutputTokens: 8192,  // actual hard cap for gemini-2.5-flash
        },
      }),
    });
  
    if (!response.ok) {
      const err = await response.json();
      throw new Error(`Gemini API error: ${err.error?.message || response.status}`);
    }
  
    const data = await response.json();
    const text = data.candidates?.[0]?.content?.parts?.[0]?.text || '';
    const truncated = data.candidates?.[0]?.finishReason === 'MAX_TOKENS';
  
    return { text, truncated };
  }
  
  // ── Main parse function with auto-continuation ─────────────────────────────
  async function parseTextWithClaude(text: string): Promise<OcrResult> {
    const MAX_CONTINUATIONS = 3;  // safety cap — prevents infinite loop
  
    // ── Initial request ──
    let { text: raw, truncated } = await callGemini(buildParsePrompt(text));
    let accumulated = raw.replace(/```json|```/g, '').trim();
  
    // ── Continue if truncated ──
    let attempts = 0;
    while (truncated && attempts < MAX_CONTINUATIONS) {
      attempts++;
      console.warn(`Gemini truncated (attempt ${attempts}), requesting continuation…`);
  
      const { text: continuation, truncated: stillTruncated } = await callGemini(
        buildContinuePrompt(text, accumulated)
      );
  
      const fragment = continuation.replace(/```json|```/g, '').trim();
      accumulated = accumulated + fragment;
      truncated = stillTruncated;
    }
  
    if (truncated) {
      console.warn('Still truncated after max continuations — attempting JSON repair…');
      accumulated = repairTruncatedJson(accumulated);
    }
  
    // ── Parse ──
    let parsed: OcrResult;
    try {
      parsed = JSON.parse(accumulated);
    } catch {
      // Last resort: repair and try again
      try {
        parsed = JSON.parse(repairTruncatedJson(accumulated));
      } catch {
        throw new Error('Gemini returned invalid JSON after continuations. Raw: ' + accumulated.slice(0, 300));
      }
    }
  
    // ── Normalise (support old single-invoice format) ──
    if (!parsed.invoices && (parsed as any).rows) {
      parsed.invoices = [{ date: (parsed as any).date || '', rows: (parsed as any).rows }];
    }
  
    parsed.invoices = (parsed.invoices || []).map(inv => ({
      ...inv,
      rows: (inv.rows || []).map(r => ({
        ...r,
        id: uid(),
        indirect_charges: r.indirect_charges || '0',
        confidence: r.confidence || 'medium',
        row_date: inv.date,
      })),
    }));
  
    return parsed;
  }

// ─────────────────────────────────────────────────────────────────────────────
// COMBINED: Tesseract → Claude pipeline
// ─────────────────────────────────────────────────────────────────────────────

async function runHybridOcr(
  file: File,
  onProgress: (pct: number, status: string) => void
): Promise<{ result: OcrResult; extractedText: string }> {
  // Phase 1: Tesseract extracts text (0–60%)
  onProgress(0, 'Starting OCR…');
  const extractedText = await extractTextWithTesseract(file, onProgress);

  // Phase 2: Claude parses text into JSON (60–100%)
  onProgress(65, 'Sending text to Claude for parsing…');
  const result = await parseTextWithClaude(extractedText);
  onProgress(100, 'Done!');

  return { result, extractedText };
}

// ─────────────────────────────────────────────────────────────────────────────
// SUB-COMPONENTS
// ─────────────────────────────────────────────────────────────────────────────

const ConfidenceBadge: React.FC<{ level: 'high' | 'medium' | 'low' }> = ({ level }) => {
  const map = {
    high: { bg: '#DCFCE7', color: '#166534', icon: '●', label: 'High' },
    medium: { bg: '#FEF9C3', color: '#854D0E', icon: '◐', label: 'Medium' },
    low: { bg: '#FEE2E2', color: '#991B1B', icon: '○', label: 'Low' },
  };
  const s = map[level];
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: s.bg, color: s.color, fontSize: 11, fontWeight: 500, padding: '2px 8px', borderRadius: 99 }}>
      <span>{s.icon}</span>{s.label}
    </span>
  );
};

const StepBar: React.FC<{ step: number }> = ({ step }) => {
  const steps = ['Upload', 'OCR → Parse', 'Review & Edit', 'Done'];
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 0, padding: '0 24px', marginTop: 4 }}>
      {steps.map((label, i) => {
        const done = i < step;
        const active = i === step;
        return (
          <React.Fragment key={i}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
              <div style={{ width: 28, height: 28, borderRadius: '50%', background: done ? '#10B981' : active ? '#6366F1' : '#E5E7EB', color: done || active ? '#fff' : '#9CA3AF', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 600, transition: 'all 0.3s' }}>
                {done ? '✓' : i + 1}
              </div>
              <span style={{ fontSize: 11, color: active ? '#6366F1' : done ? '#10B981' : '#9CA3AF', fontWeight: active ? 600 : 400, whiteSpace: 'nowrap' }}>{label}</span>
            </div>
            {i < steps.length - 1 && (
              <div style={{ flex: 1, height: 2, background: done ? '#10B981' : '#E5E7EB', margin: '0 4px', marginBottom: 20, transition: 'all 0.3s' }} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT ... [NO CHANGES NEEDED BELOW]
// ─────────────────────────────────────────────────────────────────────────────

const OcrUploadModal: React.FC<Props> = ({
  partiesList, stocksnames, mutualsnames, onSubmit, onClose,
}) => {
  const [step, setStep] = useState(0);
  const [files, setFiles] = useState<OcrFile[]>([]);
  const [dragging, setDragging] = useState(false);
  const [selectedPartyId, setSelectedPartyId] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [batches, setBatches] = useState<InvoiceBatch[]>([]);
  const [detectedParty, setDetectedParty] = useState('');
  const [detectedCategory, setDetectedCategory] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [editingRowId, setEditingRowId] = useState<string | null>(null);
  const [scanProgress, setScanProgress] = useState(0);
  const [scanStatus, setScanStatus] = useState('');
  const [showRawText, setShowRawText] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const partyCategories = React.useMemo(() => {
    if (!selectedPartyId) return [];
    const p = partiesList.find(p => String(p.id) === String(selectedPartyId));
    return p?.categories || [];
  }, [selectedPartyId, partiesList]);

  const isMutual = selectedCategory?.toLowerCase().includes('mutual');
  const stockNameOptions = (isMutual ? mutualsnames : stocksnames).map(s => ({ value: s.name, label: s.name }));

  // ── File handling ──────────────────────────────────────────────────────────

  const addFiles = useCallback((incoming: File[]) => {
    const valid = incoming.filter(f => SUPPORTED_TYPES.includes(f.type));
    if (valid.length !== incoming.length) alert('Some files skipped. Supported: JPG, PNG, WEBP, GIF, PDF');
    setFiles(prev => [...prev, ...valid.map(f => ({
      file: f, id: uid(), status: 'pending' as const,
      preview: f.type.startsWith('image/') ? URL.createObjectURL(f) : undefined,
    }))]);
  }, []);

  const onDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault(); setDragging(false);
    addFiles(Array.from(e.dataTransfer.files));
  }, [addFiles]);

  const onFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    addFiles(Array.from(e.target.files || []));
    e.target.value = '';
  };

  // ── Run hybrid OCR pipeline ────────────────────────────────────────────────

  const handleScan = async () => {
    if (!files.length) { alert('Upload at least one file'); return; }
    setStep(1); setScanProgress(0);

    const updatedFiles = [...files];
    let firstParty = '', firstCategory = '';
    const batchMap = new Map<string, InvoiceBatch>();

    for (let i = 0; i < files.length; i++) {
      const f = files[i];
      updatedFiles[i] = { ...f, status: 'processing' };
      setFiles([...updatedFiles]);

      try {
        const { result, extractedText } = await runHybridOcr(
          f.file,
          (pct, status) => {
            // Offset progress per file
            const base = (i / files.length) * 100;
            const slice = (1 / files.length) * 100;
            setScanProgress(Math.round(base + (pct / 100) * slice));
            setScanStatus(`[${f.file.name}] ${status}`);
          }
        );

        updatedFiles[i] = { ...updatedFiles[i], status: 'done', result, extractedText };
        setFiles([...updatedFiles]);

        if (!firstParty && result.party_name) firstParty = result.party_name;
        if (!firstCategory && result.category) firstCategory = result.category;

        for (const inv of result.invoices || []) {
          const dateKey = inv.date || 'unknown';
          if (!batchMap.has(dateKey)) {
            batchMap.set(dateKey, { id: uid(), date: dateKey, invoice_no: inv.invoice_no || undefined, rows: [], expanded: true });
          }
          batchMap.get(dateKey)!.rows.push(...inv.rows);
        }

      } catch (err: any) {
        updatedFiles[i] = { ...updatedFiles[i], status: 'error', error: err.message };
        setFiles([...updatedFiles]);
      }
    }

    const sortedBatches = [...batchMap.values()].sort((a, b) => a.date.localeCompare(b.date));
    setBatches(sortedBatches);
    setDetectedParty(firstParty);
    setDetectedCategory(firstCategory);

    if (firstParty) {
      const match = partiesList.find(p =>
        p.name.toLowerCase().includes(firstParty.toLowerCase()) ||
        firstParty.toLowerCase().includes(p.name.toLowerCase())
      );
      if (match) setSelectedPartyId(String(match.id));
    }
    if (firstCategory) setSelectedCategory(firstCategory);

    setScanStatus('');
    setScanProgress(100);
    setStep(2);
  };

  // ── Batch & row editing ────────────────────────────────────────────────────

  const toggleBatch = (id: string) =>
    setBatches(prev => prev.map(b => b.id === id ? { ...b, expanded: !b.expanded } : b));

  const updateBatchDate = (id: string, date: string) =>
    setBatches(prev => prev.map(b => b.id === id ? { ...b, date } : b));

  const deleteBatch = (id: string) =>
    setBatches(prev => prev.filter(b => b.id !== id));

  const updateRow = (batchId: string, rowId: string, patch: Partial<OcrExtractedRow>) => {
    setBatches(prev => prev.map(b => {
      if (b.id !== batchId) return b;
      return {
        ...b,
        rows: b.rows.map(r => {
          if (r.id !== rowId) return r;
          const updated = { ...r, ...patch, edited: true };
          if ('unit' in patch || 'total_amount' in patch || 'indirect_charges' in patch) {
            const u = safeNum(updated.unit), t = safeNum(updated.total_amount), ind = safeNum(updated.indirect_charges);
            updated.per_unit_charge = u > 0 ? ((t + ind) / u).toFixed(2) : '0';
          }
          return updated;
        }),
      };
    }));
  };

  const deleteRow = (batchId: string, rowId: string) =>
    setBatches(prev => prev.map(b =>
      b.id === batchId ? { ...b, rows: b.rows.filter(r => r.id !== rowId) } : b
    ));

  const addManualRow = (batchId: string) =>
    setBatches(prev => prev.map(b =>
      b.id === batchId ? {
        ...b, rows: [...b.rows, { id: uid(), stock_name: '', unit: '', total_amount: '', indirect_charges: '0', per_unit_charge: '', confidence: 'high', edited: true }]
      } : b
    ));

  const addManualBatch = () =>
    setBatches(prev => [...prev, { id: uid(), date: new Date().toISOString().split('T')[0], rows: [], expanded: true }]);

  // ── Submit ─────────────────────────────────────────────────────────────────

  const handleSubmit = async () => {
    if (!selectedPartyId) { alert('Select a party'); return; }
    if (!selectedCategory) { alert('Select a category'); return; }
    const activeBatches = batches
      .filter(b => b.date && b.rows.length > 0)
      .map(b => ({ date: b.date, rows: b.rows.filter(r => r.stock_name?.trim()) }))
      .filter(b => b.rows.length > 0);
    if (!activeBatches.length) { alert('No valid data to submit'); return; }
    setSubmitting(true);
    try {
      await onSubmit({ partyId: selectedPartyId, category: selectedCategory, batches: activeBatches });
      setStep(3);
    } catch (err: any) {
      alert('Submit error: ' + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // ── Totals ─────────────────────────────────────────────────────────────────

  const grandTotal = batches.reduce((sum, b) =>
    sum + b.rows.reduce((s, r) => s + safeNum(r.total_amount) + safeNum(r.indirect_charges), 0), 0
  );
  const totalRows = batches.reduce((sum, b) => sum + b.rows.length, 0);

  // ─────────────────────────────────────────────────────────────────────────────
  // STYLES
  // ─────────────────────────────────────────────────────────────────────────────

  const modalStyle: React.CSSProperties = {
    position: 'fixed', inset: 0, zIndex: 50,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(6px)', padding: '1rem',
  };

  const cardStyle: React.CSSProperties = {
    background: '#fff', borderRadius: 20,
    boxShadow: '0 25px 60px rgba(0,0,0,0.2)',
    width: '100%', maxWidth: step === 2 ? 1100 : 600,
    maxHeight: '92vh', display: 'flex', flexDirection: 'column',
    overflow: 'hidden', transition: 'max-width 0.4s',
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // HEADER (defined inside component to access `step`)
  // ─────────────────────────────────────────────────────────────────────────────

  const Header = () => (
    <div style={{ background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 50%, #4338CA 100%)', padding: '20px 24px 16px', flexShrink: 0 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
            <div style={{ background: 'rgba(255,255,255,0.15)', borderRadius: 10, padding: '6px 10px', fontSize: 20 }}>🔍</div>
            <div>
              <h2 style={{ color: '#fff', fontSize: 18, fontWeight: 700, margin: 0 }}>AI Document Scanner</h2>
              <p style={{ color: '#a5b4fc', fontSize: 12, margin: 0, marginTop: 2 }}>
                Tesseract OCR → Claude Parser · Multi-invoice · Per-date batching
              </p>
            </div>
          </div>
          <StepBar step={step} />
        </div>
        <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: 8, color: '#fff', padding: '6px 10px', cursor: 'pointer', fontSize: 18 }}>×</button>
      </div>
    </div>
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // STEP 0: UPLOAD
  // ─────────────────────────────────────────────────────────────────────────────

  if (step === 0) return (
    <div style={modalStyle}>
      <div style={cardStyle}>
        <Header />
        <div style={{ overflowY: 'auto', flex: 1, padding: 24 }}>
          <div
            onDragOver={e => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={onDrop}
            onClick={() => fileInputRef.current?.click()}
            style={{ border: `2px dashed ${dragging ? '#6366F1' : '#C7D2FE'}`, borderRadius: 16, padding: 40, textAlign: 'center', cursor: 'pointer', transition: 'all 0.2s', background: dragging ? '#EEF2FF' : '#F5F7FF' }}
          >
            <div style={{ fontSize: 48, marginBottom: 12 }}>📄</div>
            <p style={{ fontSize: 16, fontWeight: 600, color: '#3730A3', margin: 0 }}>Drop documents here or click to browse</p>
            <p style={{ fontSize: 13, color: '#6B7280', marginTop: 8 }}>
              Multi-page PDFs · Images · Scanned invoices
            </p>
            <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
              {['PDF', 'JPG', 'PNG', 'WEBP'].map(ext => (
                <span key={ext} style={{ background: '#E0E7FF', color: '#3730A3', borderRadius: 6, padding: '2px 10px', fontSize: 12, fontWeight: 600 }}>{ext}</span>
              ))}
            </div>
          </div>
          <input ref={fileInputRef} type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.webp,.gif" style={{ display: 'none' }} onChange={onFileInput} />

          {files.length > 0 && (
            <div style={{ marginTop: 20 }}>
              <p style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 10 }}>{files.length} file{files.length !== 1 ? 's' : ''} ready</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {files.map((f, i) => (
                  <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 12, background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 12, padding: '10px 14px' }}>
                    {f.preview
                      ? <img src={f.preview} alt="" style={{ width: 44, height: 44, borderRadius: 8, objectFit: 'cover' }} />
                      : <div style={{ width: 44, height: 44, borderRadius: 8, background: '#EEF2FF', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>📋</div>
                    }
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ margin: 0, fontSize: 13, fontWeight: 500, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.file.name}</p>
                      <p style={{ margin: 0, fontSize: 12, color: '#6B7280' }}>{(f.file.size / 1024).toFixed(1)} KB · {f.file.type.split('/')[1].toUpperCase()}</p>
                    </div>
                    <button onClick={() => setFiles(prev => prev.filter((_, ii) => ii !== i))} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: '#9CA3AF', padding: 4 }}>×</button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* How it works callouts */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginTop: 24 }}>
            {[
              { icon: '🔠', title: 'Tesseract OCR', desc: 'Free, runs in browser — extracts raw text from your document' },
              { icon: '🤖', title: 'Claude parses', desc: 'Text sent to Claude to extract structured invoice data' },
              { icon: '📅', title: 'Per-date batches', desc: 'Each invoice date becomes a separate importable batch' },
            ].map(item => (
              <div key={item.title} style={{ background: '#F9FAFB', borderRadius: 12, padding: '14px 12px', border: '1px solid #F3F4F6' }}>
                <div style={{ fontSize: 24, marginBottom: 6 }}>{item.icon}</div>
                <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: '#111827' }}>{item.title}</p>
                <p style={{ margin: 0, fontSize: 12, color: '#6B7280', marginTop: 3 }}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>

        <div style={{ padding: '16px 24px', borderTop: '1px solid #F3F4F6', background: '#FAFAFA', flexShrink: 0 }}>
          <button
            onClick={handleScan}
            disabled={!files.length}
            style={{ width: '100%', padding: '13px 0', borderRadius: 12, border: 'none', cursor: files.length ? 'pointer' : 'not-allowed', background: files.length ? 'linear-gradient(135deg, #6366F1, #4F46E5)' : '#E5E7EB', color: files.length ? '#fff' : '#9CA3AF', fontSize: 15, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
          >
            <span>🔍</span> Scan Document →
          </button>
        </div>
      </div>
    </div>
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // STEP 1: SCANNING PROGRESS
  // ─────────────────────────────────────────────────────────────────────────────

  if (step === 1) return (
    <div style={modalStyle}>
      <div style={{ ...cardStyle, maxWidth: 520 }}>
        <Header />
        <div style={{ padding: 40, textAlign: 'center', flex: 1 }}>
          <div style={{ fontSize: 64, marginBottom: 20 }}>{scanProgress < 100 ? '⚙️' : '✅'}</div>
          <h3 style={{ fontSize: 20, fontWeight: 700, color: '#1F2937', margin: 0, marginBottom: 8 }}>
            {scanProgress < 100 ? 'Processing your document…' : 'All done!'}
          </h3>
          <p style={{ fontSize: 14, color: '#6B7280', marginBottom: 28 }}>{scanStatus || 'Starting…'}</p>

          <div style={{ background: '#E5E7EB', borderRadius: 99, height: 10, overflow: 'hidden', marginBottom: 12 }}>
            <div style={{ background: 'linear-gradient(90deg, #6366F1, #818CF8)', height: '100%', width: `${scanProgress}%`, transition: 'width 0.4s ease', borderRadius: 99 }} />
          </div>
          <p style={{ fontSize: 13, color: '#6366F1', fontWeight: 600 }}>{scanProgress}%</p>

          {/* Two-phase indicator */}
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', marginTop: 20 }}>
            {[
              { label: '① Tesseract OCR', desc: 'Extracting text from document', done: scanProgress >= 65 },
              { label: '② Claude AI', desc: 'Parsing into invoice data', done: scanProgress >= 100 },
            ].map(phase => (
              <div key={phase.label} style={{ background: phase.done ? '#ECFDF5' : '#F9FAFB', border: `1px solid ${phase.done ? '#A7F3D0' : '#E5E7EB'}`, borderRadius: 10, padding: '10px 16px', textAlign: 'left', flex: 1 }}>
                <p style={{ margin: 0, fontSize: 12, fontWeight: 700, color: phase.done ? '#065F46' : '#374151' }}>{phase.done ? '✓ ' : ''}{phase.label}</p>
                <p style={{ margin: 0, fontSize: 11, color: '#6B7280', marginTop: 2 }}>{phase.desc}</p>
              </div>
            ))}
          </div>

          <div style={{ marginTop: 24, textAlign: 'left' }}>
            {files.map(f => (
              <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid #F3F4F6' }}>
                <span style={{ fontSize: 16 }}>
                  {f.status === 'done' ? '✅' : f.status === 'error' ? '❌' : f.status === 'processing' ? '⏳' : '⬜'}
                </span>
                <span style={{ fontSize: 13, color: '#374151', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.file.name}</span>
                {f.status === 'done' && f.result && (
                  <span style={{ fontSize: 12, color: '#10B981', fontWeight: 500 }}>
                    {f.result.invoices?.length || 0} invoice{(f.result.invoices?.length || 0) !== 1 ? 's' : ''}
                  </span>
                )}
                {f.status === 'error' && <span style={{ fontSize: 12, color: '#EF4444' }}>Failed</span>}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // STEP 2: REVIEW
  // ─────────────────────────────────────────────────────────────────────────────

  if (step === 2) {
    return (
      <div style={modalStyle}>
        <div style={cardStyle}>
          <Header />
          <div style={{ overflowY: 'auto', flex: 1 }}>

            {/* Config bar */}
            <div style={{ padding: '16px 24px', background: '#F8F9FF', borderBottom: '1px solid #E5E7EB', display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              {(detectedParty || detectedCategory) && (
                <div style={{ width: '100%', display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                  <span style={{ fontSize: 12, color: '#6B7280', fontWeight: 500 }}>AI detected:</span>
                  {detectedParty && <span style={{ background: '#ECFDF5', color: '#065F46', borderRadius: 6, padding: '2px 10px', fontSize: 12, fontWeight: 500 }}>Party: {detectedParty}</span>}
                  {detectedCategory && <span style={{ background: '#EEF2FF', color: '#3730A3', borderRadius: 6, padding: '2px 10px', fontSize: 12, fontWeight: 500 }}>Category: {detectedCategory}</span>}
                  {/* Show raw text button */}
                  {files.some(f => f.extractedText) && (
                    <button
                      onClick={() => setShowRawText(files.find(f => f.extractedText)?.extractedText || null)}
                      style={{ background: '#FFF7ED', color: '#9A3412', border: '1px solid #FED7AA', borderRadius: 6, padding: '2px 10px', fontSize: 12, fontWeight: 500, cursor: 'pointer' }}
                    >
                      🔠 View raw OCR text
                    </button>
                  )}
                </div>
              )}
              <div style={{ flex: 1, minWidth: 180 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Party <span style={{ color: '#EF4444' }}>*</span></label>
                <select value={selectedPartyId} onChange={e => { setSelectedPartyId(e.target.value); setSelectedCategory(''); }} style={{ width: '100%', border: '1px solid #D1D5DB', borderRadius: 8, padding: '7px 10px', fontSize: 13, outline: 'none', color: '#111827' }}>
                  <option value="">Choose party</option>
                  {partiesList.map(p => <option key={p.id} value={String(p.id)}>{p.name}</option>)}
                </select>
              </div>
              <div style={{ flex: 1, minWidth: 160 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Category <span style={{ color: '#EF4444' }}>*</span></label>
                <select value={selectedCategory} onChange={e => setSelectedCategory(e.target.value)} disabled={!selectedPartyId} style={{ width: '100%', border: '1px solid #D1D5DB', borderRadius: 8, padding: '7px 10px', fontSize: 13, outline: 'none', color: '#111827', background: !selectedPartyId ? '#F9FAFB' : '#fff' }}>
                  <option value="">Select category</option>
                  {partyCategories.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>

            {/* Stats */}
            <div style={{ display: 'flex', gap: 12, padding: '12px 24px', background: '#fff', borderBottom: '1px solid #F3F4F6', flexWrap: 'wrap' }}>
              {[
                { label: 'Invoices', value: batches.length, color: '#3730A3' },
                { label: 'Total rows', value: totalRows, color: '#1F2937' },
                { label: 'Grand total', value: `₹${grandTotal.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`, color: '#065F46' },
              ].map(s => (
                <div key={s.label} style={{ background: '#F9FAFB', borderRadius: 10, padding: '8px 16px', border: '1px solid #F3F4F6' }}>
                  <p style={{ margin: 0, fontSize: 11, color: '#6B7280' }}>{s.label}</p>
                  <p style={{ margin: 0, fontSize: 16, fontWeight: 700, color: s.color }}>{s.value}</p>
                </div>
              ))}
            </div>

            {/* Raw text modal */}
            {showRawText && (
              <div style={{ position: 'fixed', inset: 0, zIndex: 100, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
                <div style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 700, maxHeight: '80vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                  <div style={{ padding: '16px 20px', borderBottom: '1px solid #E5E7EB', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#111827' }}>🔠 Raw Tesseract OCR Text</h3>
                    <button onClick={() => setShowRawText(null)} style={{ background: '#F3F4F6', border: 'none', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: 14 }}>Close</button>
                  </div>
                  <pre style={{ flex: 1, overflowY: 'auto', padding: 20, margin: 0, fontSize: 12, color: '#374151', lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word', background: '#F9FAFB' }}>
                    {showRawText}
                  </pre>
                </div>
              </div>
            )}

            {/* Batches */}
            <div style={{ padding: '16px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
              {batches.map((batch, bi) => {
                const batchTotal = batch.rows.reduce((s, r) => s + safeNum(r.total_amount) + safeNum(r.indirect_charges), 0);
                return (
                  <div key={batch.id} style={{ border: '1.5px solid #E5E7EB', borderRadius: 14, overflow: 'hidden' }}>
                    {/* Batch header */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', background: '#F8F9FF', borderBottom: batch.expanded ? '1px solid #E5E7EB' : 'none', flexWrap: 'wrap' }}>
                      <button onClick={() => toggleBatch(batch.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: '#6366F1', padding: 0 }}>
                        {batch.expanded ? '▼' : '▶'}
                      </button>
                      <span style={{ background: '#EEF2FF', color: '#3730A3', borderRadius: 6, padding: '2px 10px', fontSize: 12, fontWeight: 700 }}>
                        Invoice #{batch.invoice_no || bi + 1}
                      </span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ fontSize: 12, color: '#6B7280', fontWeight: 500 }}>📅</span>
                        <input type="date" value={batch.date} onChange={e => updateBatchDate(batch.id, e.target.value)} style={{ border: '1px solid #D1D5DB', borderRadius: 7, padding: '4px 8px', fontSize: 13, outline: 'none', color: '#111827' }} />
                      </div>
                      <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12 }}>
                        <span style={{ fontSize: 13, color: '#374151' }}>
                          <strong>{batch.rows.length}</strong> rows · <strong style={{ color: '#065F46' }}>₹{batchTotal.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</strong>
                        </span>
                        <button onClick={() => deleteBatch(batch.id)} style={{ background: '#FEF2F2', border: 'none', borderRadius: 7, padding: '4px 10px', cursor: 'pointer', color: '#991B1B', fontSize: 12, fontWeight: 500 }}>
                          🗑 Delete
                        </button>
                      </div>
                    </div>

                    {/* Rows table */}
                    {batch.expanded && (
                      <>
                        <div style={{ overflowX: 'auto' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                            <thead>
                              <tr style={{ background: '#F9FAFB', borderBottom: '1px solid #E5E7EB' }}>
                                <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 600, color: '#374151', width: 28 }}>#</th>
                                <th style={{ textAlign: 'left', padding: '8px 8px', fontWeight: 600, color: '#374151', minWidth: 200 }}>Stock / Fund Name</th>
                                <th style={{ textAlign: 'right', padding: '8px 8px', fontWeight: 600, color: '#374151', minWidth: 80 }}>Unit</th>
                                <th style={{ textAlign: 'right', padding: '8px 8px', fontWeight: 600, color: '#374151', minWidth: 110 }}>Total (₹)</th>
                                <th style={{ textAlign: 'right', padding: '8px 8px', fontWeight: 600, color: '#374151', minWidth: 100 }}>Charges (₹)</th>
                                <th style={{ textAlign: 'right', padding: '8px 8px', fontWeight: 600, color: '#374151', minWidth: 90 }}>Per Unit</th>
                                <th style={{ textAlign: 'center', padding: '8px 8px', fontWeight: 600, color: '#374151', width: 90 }}>Confidence</th>
                                <th style={{ textAlign: 'center', padding: '8px 8px', fontWeight: 600, color: '#374151', width: 70 }}>Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              {batch.rows.map((row, ri) => {
                                const isEditing = editingRowId === row.id;
                                return (
                                  <tr key={row.id} style={{ borderBottom: '1px solid #F3F4F6', background: row.confidence === 'low' ? '#FFFBEB' : row.edited ? '#F0FDF4' : '#fff' }}>
                                    <td style={{ padding: '7px 12px', color: '#9CA3AF' }}>{ri + 1}</td>
                                    <td style={{ padding: '7px 8px' }}>
                                      {isEditing ? (
                                        <Select
                                          options={stockNameOptions}
                                          value={row.stock_name ? { value: row.stock_name, label: row.stock_name } : null}
                                          onChange={sel => updateRow(batch.id, row.id, { stock_name: sel?.value || '' })}
                                          onInputChange={input => input && updateRow(batch.id, row.id, { stock_name: input })}
                                          placeholder="Search or type…"
                                          menuPortalTarget={document.body}
                                          styles={{ control: b => ({ ...b, fontSize: 13, minHeight: 32, borderRadius: 6, borderColor: '#6366F1' }), menu: b => ({ ...b, zIndex: 9999 }) }}
                                        />
                                      ) : (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                          <span style={{ fontWeight: 500, color: '#111827' }}>{row.stock_name || <em style={{ color: '#9CA3AF' }}>—</em>}</span>
                                          {row.edited && <span title="Edited" style={{ fontSize: 10, color: '#10B981' }}>✎</span>}
                                        </div>
                                      )}
                                    </td>
                                    <td style={{ padding: '7px 8px', textAlign: 'right' }}>
                                      {isEditing
                                        ? <input type="number" value={row.unit} onChange={e => updateRow(batch.id, row.id, { unit: e.target.value })} style={{ width: 72, border: '1px solid #6366F1', borderRadius: 6, padding: '4px 6px', fontSize: 13, textAlign: 'right' }} />
                                        : <span style={{ color: '#374151' }}>{row.unit || '—'}</span>
                                      }
                                    </td>
                                    <td style={{ padding: '7px 8px', textAlign: 'right' }}>
                                      {isEditing
                                        ? <input type="number" value={row.total_amount} onChange={e => updateRow(batch.id, row.id, { total_amount: e.target.value })} style={{ width: 100, border: '1px solid #6366F1', borderRadius: 6, padding: '4px 6px', fontSize: 13, textAlign: 'right' }} />
                                        : <span style={{ color: '#111827', fontWeight: 500 }}>₹{safeNum(row.total_amount).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>
                                      }
                                    </td>
                                    <td style={{ padding: '7px 8px', textAlign: 'right' }}>
                                      {isEditing
                                        ? <input type="number" value={row.indirect_charges} onChange={e => updateRow(batch.id, row.id, { indirect_charges: e.target.value })} style={{ width: 88, border: '1px solid #6366F1', borderRadius: 6, padding: '4px 6px', fontSize: 13, textAlign: 'right' }} />
                                        : <span style={{ color: '#6B7280' }}>₹{safeNum(row.indirect_charges).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>
                                      }
                                    </td>
                                    <td style={{ padding: '7px 8px', textAlign: 'right' }}>
                                      <span style={{ color: '#6B7280', fontSize: 12 }}>{row.per_unit_charge ? `₹${safeNum(row.per_unit_charge).toFixed(2)}` : '—'}</span>
                                    </td>
                                    <td style={{ padding: '7px 8px', textAlign: 'center' }}>
                                      <ConfidenceBadge level={row.confidence} />
                                    </td>
                                    <td style={{ padding: '7px 8px', textAlign: 'center' }}>
                                      <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                                        <button title={isEditing ? 'Done' : 'Edit'} onClick={() => setEditingRowId(isEditing ? null : row.id)} style={{ background: isEditing ? '#ECFDF5' : '#EEF2FF', border: 'none', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', color: isEditing ? '#065F46' : '#3730A3', fontSize: 13 }}>
                                          {isEditing ? '✓' : '✏'}
                                        </button>
                                        <button title="Delete row" onClick={() => deleteRow(batch.id, row.id)} style={{ background: '#FEF2F2', border: 'none', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', color: '#991B1B', fontSize: 13 }}>✕</button>
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                        <div style={{ padding: '10px 16px' }}>
                          <button onClick={() => addManualRow(batch.id)} style={{ background: 'none', border: '1.5px dashed #C7D2FE', borderRadius: 8, padding: '6px 16px', color: '#6366F1', fontSize: 12, fontWeight: 600, cursor: 'pointer', width: '100%' }}>
                            + Add row to this invoice
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                );
              })}

              <button onClick={addManualBatch} style={{ background: 'none', border: '1.5px dashed #A5B4FC', borderRadius: 12, padding: '10px 20px', color: '#6366F1', fontSize: 13, fontWeight: 600, cursor: 'pointer', width: '100%' }}>
                + Add invoice batch manually
              </button>
            </div>
          </div>

          {/* Footer */}
          <div style={{ padding: '14px 24px', borderTop: '1px solid #F3F4F6', background: '#FAFAFA', flexShrink: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <button onClick={() => setStep(0)} style={{ background: 'none', border: '1px solid #D1D5DB', borderRadius: 10, padding: '9px 20px', color: '#374151', fontSize: 14, fontWeight: 500, cursor: 'pointer' }}>
              ← Re-scan
            </button>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ fontSize: 13, color: '#6B7280' }}>
                {batches.length} invoices · {totalRows} rows · <strong style={{ color: '#111827' }}>₹{grandTotal.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</strong>
              </span>
              <button
                onClick={handleSubmit}
                disabled={submitting || !selectedPartyId || !selectedCategory}
                style={{ background: submitting || !selectedPartyId || !selectedCategory ? '#E5E7EB' : 'linear-gradient(135deg, #10B981, #059669)', border: 'none', borderRadius: 10, padding: '9px 28px', color: submitting || !selectedPartyId || !selectedCategory ? '#9CA3AF' : '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}
              >
                {submitting ? 'Importing…' : `Import ${batches.length} invoices →`}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // STEP 3: SUCCESS
  // ─────────────────────────────────────────────────────────────────────────────

  return (
    <div style={modalStyle}>
      <div style={{ ...cardStyle, maxWidth: 460, textAlign: 'center' }}>
        <Header />
        <div style={{ padding: 48, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
          <div style={{ fontSize: 64 }}>🎉</div>
          <h3 style={{ fontSize: 22, fontWeight: 700, color: '#111827', margin: 0 }}>Import successful!</h3>
          <p style={{ fontSize: 14, color: '#6B7280', margin: 0 }}>
            {batches.length} invoice batches · {totalRows} rows imported
          </p>
          <div style={{ background: '#ECFDF5', border: '1px solid #A7F3D0', borderRadius: 12, padding: '14px 20px', marginTop: 8 }}>
            <p style={{ margin: 0, fontSize: 13, color: '#065F46' }}>
              Total amount: <strong>₹{grandTotal.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</strong>
            </p>
          </div>
          <button onClick={onClose} style={{ background: 'linear-gradient(135deg, #6366F1, #4F46E5)', border: 'none', borderRadius: 10, padding: '11px 32px', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer', marginTop: 8 }}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export default OcrUploadModal;

// ─────────────────────────────────────────────────────────────────────────────
// SETUP — install dependencies
// ─────────────────────────────────────────────────────────────────────────────
/*
  npm install tesseract.js pdfjs-dist

  IMPORTANT: For PDF support, copy the worker file after installing pdfjs-dist:
    cp node_modules/pdfjs-dist/build/pdf.worker.min.js public/pdf.worker.min.js

  This will prevent CORS and dynamic import errors when processing PDF files.
*/